import { createFileRoute } from '@tanstack/react-router'
import { Button, Flex, Form, Tabs } from 'antd'
import { useTranslation } from 'react-i18next'
import { CampaignPublicForm } from '../public-form/components/campaign-public-form'
import React, { useEffect, useState } from 'react'
import { useAuth } from '../auth'
import { CampaignLeadFormSection } from '../campaign/components/settings/campaign-lead-form-section'
import { useCampaign } from '../campaign/hooks'
import { useCheckCampaignFacebookLeadSetup } from '../facebook/hooks'
import { SetupCheckResult } from '../facebook/setup-check'
import { useManagedForm } from '../public-form/hooks'
import { NetworkErrors } from '../components/network-errors'

export const Route = createFileRoute('/_auth/_main/campaigns/$campaignId/settings/candidate-sources')({
    component: CampaignSettingsSources,
})

function CampaignSettingsSources() {
    const { campaignId } = Route.useParams()
    const { source } = Route.useSearch()
    const navigate = Route.useNavigate()
    const { t } = useTranslation()
    const campaign = useCampaign(campaignId)
    const publicForm = useManagedForm(campaignId, source === undefined)
    const publicFormUsed = publicForm.data?.enabled === true && publicForm.data.published !== null
    const facebookUsed = !!campaign.data?.formId
    const defaultSource = facebookUsed && !publicFormUsed ? 'facebook' : 'public'
    const sourcesLoaded = !campaign.isPending && !publicForm.isPending
    useEffect(() => {
        if (source !== undefined || !sourcesLoaded) return
        // Set the initial choice once, without adding a browser history entry.
        void navigate({
            search: (previous) => ({ ...previous, source: defaultSource }),
            replace: true,
            resetScroll: false,
        })
    }, [source, sourcesLoaded, defaultSource, navigate])
    if (!source && campaign.isPending) return <NetworkErrors query={campaign} />
    if (!source && publicForm.isPending) return <NetworkErrors query={publicForm} />
    return (
        <Tabs
            key={campaignId}
            activeKey={source ?? defaultSource}
            onChange={(value) => {
                if (value !== 'public' && value !== 'facebook') return
                void navigate({
                    search: (previous) => ({ ...previous, source: value }),
                    resetScroll: false,
                })
            }}
            items={[
                {
                    key: 'public',
                    label: t('Public form'),
                    children: (
                        <CampaignPublicForm
                            key={campaignId}
                            campaignId={campaignId}
                        />
                    ),
                },
                {
                    key: 'facebook',
                    label: t('Facebook lead form'),
                    children: (
                        <Flex
                            vertical
                            align="start"
                            gap="small"
                        >
                            <CampaignSettingsLeadForm />
                            <Button
                                type="primary"
                                htmlType="submit"
                            >
                                {t('Save Facebook settings')}
                            </Button>
                        </Flex>
                    ),
                },
            ]}
        />
    )
}

function CampaignSettingsLeadForm() {
    const { campaignId } = Route.useParams()
    const { activeCompany } = useAuth()
    const campaign = useCampaign(campaignId)
    const checkLeadSetup = useCheckCampaignFacebookLeadSetup(campaignId)
    const form = Form.useFormInstance()
    const [leadSetupResult, setLeadSetupResult] = useState<SetupCheckResult | null>(null)
    const accountId = Form.useWatch('accountId', form)
    const formId = Form.useWatch('formId', form)

    useEffect(() => {
        setLeadSetupResult(null)
    }, [accountId, formId])

    if (campaign.isPending || campaign.isError) {
        return null
    }

    return (
        <CampaignLeadFormSection
            campaignId={campaign.data.id}
            facebookConnected={activeCompany?.facebookConnected}
            accountId={accountId}
            leadSetupResult={leadSetupResult}
            isCheckingLeadSetup={checkLeadSetup.isPending}
            onAccountClear={() => {
                form.setFieldValue('formId', undefined)
            }}
            onValidateLeadSetup={() =>
                checkLeadSetup
                    .mutateAsync({
                        accountId: accountId ?? undefined,
                        formId: formId ?? undefined,
                    })
                    .then((checkResult) => {
                        if (checkResult) {
                            setLeadSetupResult(checkResult)
                        }
                    })
            }
        />
    )
}
